import { useEffect, useState } from "react";
import { api, CATEGORIES, CATEGORY_LABELS } from "@/lib/api";
import { todayLocal } from "@/lib/date";
import { useDemo } from "@/context/DemoContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, Funnel, MagnifyingGlass, FilePdf, UserCircle, FileText, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

// A factory, not a constant: a module-level object would evaluate its date once
// at import, so every reset would reuse the page-load day forever.
const emptyForm = () => ({ property_id: "", unit_id: "", category: "repairs", amount: "", date: todayLocal(), description: "", vendor: "" });

const fmtTs = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function Transactions() {
  const { fmtMoney } = useDemo();
  const [txns, setTxns] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filterProp, setFilterProp] = useState("all");
  const [trace, setTrace] = useState(null);

  // Re-read the clock every time the dialog opens. The lazy useState initializer
  // only runs at mount, so a tab left open across midnight would otherwise keep
  // offering the mount day and file entries under the wrong date. Only the date
  // is refreshed — anything already typed into the other fields survives.
  const openForm = (isOpen) => {
    if (isOpen) setForm((f) => ({ ...f, date: todayLocal() }));
    setOpen(isOpen);
  };

  const openSource = async (billId) => {
    try {
      const r = await api.get(`/bills/${billId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Source document not available"); }
  };

  const load = () => {
    const q = filterProp !== "all" ? `?property_id=${filterProp}` : "";
    api.get(`/transactions${q}`).then((r) => setTxns(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/properties").then((r) => setProps(r.data)); }, []);
  useEffect(() => { load(); }, [filterProp]);

  const propName = (pid) => props.find((p) => p.id === pid)?.name || "—";
  const unitsFor = (pid) => props.find((p) => p.id === pid)?.units || [];

  const submit = async () => {
    if (!form.property_id) { toast.error("Select a property"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount"); return; }
    try {
      const type = form.category === "rent" ? "income" : "expense";
      await api.post("/transactions", {
        ...form, type, amount: Number(form.amount),
        unit_id: form.unit_id || null,
      });
      toast.success("Transaction added");
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error("Failed to add transaction"); }
  };

  const remove = async (id) => {
    await api.delete(`/transactions/${id}`);
    load();
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">General</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Ledger</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Funnel size={16} className="text-muted-foreground" />
            <Select value={filterProp} onValueChange={setFilterProp}>
              <SelectTrigger data-testid="filter-property-select" className="rounded-sm w-44 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={open} onOpenChange={openForm}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-button" className="rounded-sm font-semibold transition-transform hover:-translate-y-[1px]"><Plus size={16} className="mr-1" /> Entry</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">New Entry</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Property</Label>
                  <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v, unit_id: "" })}>
                    <SelectTrigger data-testid="txn-property-select" className="rounded-sm mt-1.5"><SelectValue placeholder="Select property" /></SelectTrigger>
                    <SelectContent>
                      {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {unitsFor(form.property_id).length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Unit (optional)</Label>
                    <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="txn-unit-select" className="rounded-sm mt-1.5"><SelectValue placeholder="Property-level" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Property-level (no unit)</SelectItem>
                        {unitsFor(form.property_id).map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="txn-category-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Amount</Label>
                    <Input data-testid="txn-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm mt-1.5" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Date</Label>
                    <Input data-testid="txn-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Vendor</Label>
                    <Input data-testid="txn-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="rounded-sm mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Description</Label>
                  <Input data-testid="txn-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1.5" />
                </div>
              </div>
              <DialogFooter><Button data-testid="save-transaction-button" onClick={submit} className="rounded-sm font-semibold w-full">Save Entry</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {txns.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No transactions yet. Add an entry or process a bill from the inbox.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-bold p-4">Date</th>
                  <th className="text-left font-bold p-4">Property</th>
                  <th className="text-left font-bold p-4 hidden sm:table-cell">Category</th>
                  <th className="text-left font-bold p-4 hidden md:table-cell">Description</th>
                  <th className="text-right font-bold p-4">Amount</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} data-testid={`txn-row-${t.id}`} className="border-b border-border last:border-0 hover:bg-secondary group">
                    <td className="p-4 font-mono text-xs whitespace-nowrap">{t.date}</td>
                    <td className="p-4 font-semibold">{propName(t.property_id)}</td>
                    <td className="p-4 hidden sm:table-cell">{CATEGORY_LABELS[t.category] || t.category}</td>
                    <td className="p-4 hidden md:table-cell text-muted-foreground">
                      <span className="flex items-center gap-2">
                        {t.source === "bill" ? <FilePdf size={14} className="shrink-0" /> : <FileText size={14} className="shrink-0" />}
                        {t.description || t.vendor || "—"}
                      </span>
                    </td>
                    <td className={`p-4 text-right font-mono tabular-nums font-semibold ${t.type === "income" ? "text-success" : "text-destructive"}`}>
                      {t.type === "income" ? "+" : "−"}{fmtMoney(t.amount)}
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button data-testid={`trace-txn-${t.id}`} title="Trace source" onClick={() => setTrace(t)} className="text-muted-foreground hover:text-foreground p-1 transition-colors">
                        <MagnifyingGlass size={16} />
                      </button>
                      <button data-testid={`delete-txn-${t.id}`} onClick={() => remove(t.id)} className="text-muted-foreground hover:text-destructive p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Trace-to-source dialog */}
      <Dialog open={!!trace} onOpenChange={(o) => !o && setTrace(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
              <MagnifyingGlass size={20} weight="bold" /> Entry Source
            </DialogTitle>
          </DialogHeader>
          {trace && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Property</p><p className="font-semibold">{propName(trace.property_id)}</p></div>
                <div><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Amount</p><p className={`font-mono font-bold ${trace.type === "income" ? "text-success" : "text-destructive"}`}>{trace.type === "income" ? "+" : "−"}{fmtMoney(trace.amount)}</p></div>
                <div><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Category</p><p>{CATEGORY_LABELS[trace.category] || trace.category}</p></div>
                <div><p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Date</p><p className="font-mono">{trace.date}</p></div>
              </div>

              {trace.source === "bill" && trace.bill_id ? (
                <div className="border border-border rounded-sm p-4 bg-secondary/40 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold"><FilePdf size={18} weight="fill" /> From statement{trace.vendor ? `: ${trace.vendor}` : ""}</div>
                  <p className="text-xs text-muted-foreground">This entry was created automatically when you approved an ingested bill. Open the original PDF to confirm exactly where this number came from.</p>
                  <Button data-testid="trace-view-document" size="sm" onClick={() => openSource(trace.bill_id)} className="rounded-sm font-semibold">
                    <ArrowSquareOut size={15} className="mr-1" /> View source document
                  </Button>
                  <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                    Posted by {trace.created_by_name || "—"} on {fmtTs(trace.created_at)}
                  </p>
                </div>
              ) : (
                <div className="border border-border rounded-sm p-4 bg-secondary/40 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold"><UserCircle size={18} weight="fill" /> Manual entry</div>
                  <p className="text-sm">
                    Entered by <span className="font-semibold">{trace.created_by_name || "—"}</span>
                    {trace.created_by_email ? <span className="text-muted-foreground"> ({trace.created_by_email})</span> : null}
                  </p>
                  <p className="text-sm text-muted-foreground">on {fmtTs(trace.created_at)}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
